/**
 * Preview — what a person sees before they turn this plugin on.
 *
 * From the backend's own words (`GET /plugins/<id>/asks-for`), so the portal and
 * Nucleus say the same thing. With unsaved edits it shows the draft, through
 * `POST /plugins/<id>/changes-preview`. Only a backend without those routes
 * falls back to reading the manifest here.
 */
import { useEffect, useState } from 'react';

import api from '../../services/api';
import { isEmptyBody, localAsksFor } from './manifest';
import { useSchemas } from './bits';
import { reapprovalLine } from './panels/panelWords';

/** A route the backend doesn't have yet (or a function the API lacks). */
export const missingRoute = (e) => !!e && (e.missing || e.status === 405
  || (e.status === 404 && /resource not found/i.test(String(e.message || ''))));

// A route found missing is not asked again until the page reloads.
const absent = new Set();

export async function call(name, ...args) {
  if (typeof api[name] !== 'function' || absent.has(name)) {
    const err = new Error(`${name} is not available`);
    err.missing = true;
    throw err;
  }
  try {
    return await api[name](...args);
  } catch (e) {
    if (missingRoute(e)) absent.add(name);
    throw e;
  }
}

const UNDONE = / · cannot be undone$/;
const sentence = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

function Row({ label, items, empty, plain }) {
  if (!items?.length && !empty) return null;
  return (
    <div className="pv__row">
      <dt>{label}</dt>
      <dd>
        {items?.length ? (
          <ul className="pv__list">
            {items.map((t, i) => {
              const undone = UNDONE.test(t);
              return (
                <li key={i}>
                  {plain ? t : sentence(t.replace(UNDONE, ''))}
                  {undone ? <span className="pv__undone">Can't be undone</span> : null}
                </li>
              );
            })}
          </ul>
        ) : <span className="pw__muted">{empty}</span>}
      </dd>
    </div>
  );
}

/** What Turn on shows a person, laid out as Nucleus lays it out. */
export function AsksFor({ parts, pluginName }) {
  if (!parts?.length) return <p className="pw__line pw__muted">It asks for nothing.</p>;
  const asksEvery = parts.some((p) => p.asks_every_run);
  const removes = parts.some((p) => p.removes_source);
  return (
    <div className="pv">
      <div className="pv__head">
        <span className="pv__title">{pluginName} asks for</span>
        {asksEvery ? <span className="pv__flag">Ask every run</span> : null}
        {removes ? <span className="pv__flag">Removes what it reads</span> : null}
      </div>
      {parts.map((p, i) => (
        <section key={p.tool_id || i} className="pv__part">
          <h3 className="pv__name">
            {p.name}
            {p.irreversible ? <span className="pv__undone">Can't be undone</span> : null}
          </h3>
          <dl className="pv__rows">
            <Row label="Reads" items={p.reads} />
            <Row label="Writes" items={p.writes} />
            <Row label="Outside ofSelf" items={p.outside} />
            <Row label="Data goes to" items={p.goes_to} plain />
          </dl>
          {!p.reads?.length && !p.writes?.length && !p.outside?.length
            ? <p className="pw__line pw__muted">Nothing in your graph, and nothing outside it.</p> : null}
        </section>
      ))}
    </div>
  );
}

/** What a save would change, as "Adds" and "No longer". */
export function Changes({ changes, toolName }) {
  const rows = (changes || []).filter((c) => c.added?.length || c.removed?.length);
  if (!rows.length) return null;
  return (
    <ul className="pv__changes">
      {rows.map((c, i) => (
        <li key={i}>
          <span className="pv__change-part">{c.tool_id ? toolName(c.tool_id) : 'The plugin'}</span>
          {(c.added || []).map((t, n) => <span key={`a${n}`} className="pv__change"><b>Adds</b> {t}</span>)}
          {(c.removed || []).map((t, n) => <span key={`r${n}`} className="pv__change"><b>No longer</b> {t}</span>)}
        </li>
      ))}
    </ul>
  );
}

export default function PreviewStep({ plugin, draft, body }) {
  const schemas = useSchemas();
  const [state, setState] = useState({ loading: true });
  const dirty = !isEmptyBody(body);
  const key = JSON.stringify(body || {});

  useEffect(() => {
    let live = true;
    setState((s) => ({ ...s, loading: true }));
    const load = dirty ? call('previewChanges', plugin.id, body).then((r) => ({
      parts: r?.asks_for?.parts || [], changes: r?.changes || [], reapproval: r?.reapproval,
      lapsing: r?.approvals_lapsing, errors: r?.valid === false ? r.errors || [] : [],
    })) : call('getAsksFor', plugin.id).then((r) => ({ parts: r?.parts || [] }));
    load
      .then((r) => { if (live) setState({ ...r, from: 'backend' }); })
      .catch((e) => { if (live) setState(missingRoute(e) ? { from: 'local' } : { error: e.message || 'Could not load it.' }); });
    return () => { live = false; };
    // `key` stands for the body.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plugin.id, key, dirty]);

  const parts = state.from === 'local' ? localAsksFor(draft, schemas.nameOf).parts : state.parts;
  const toolName = (id) => (draft.tools || []).find((t) => t.tool_id === id)?.name || id;

  return (
    <div className="pw__step">
      <h2 className="pw__h">What a person sees before they turn it on</h2>
      {dirty ? <p className="pw__line pw__note">With your unsaved edits.</p> : null}
      {state.error ? <p className="pw__err">{state.error}</p> : null}
      {state.loading && !parts ? <p className="pw__muted">Loading…</p> : null}
      {parts ? <AsksFor parts={parts} pluginName={draft.name || plugin.name} /> : null}
      {dirty && state.changes?.length ? (
        <section className="pw__sec">
          <h2 className="pw__h">What changes</h2>
          {state.reapproval ? (
            <p className="pw__line">{reapprovalLine(state.lapsing)}</p>
          ) : <p className="pw__line">Nobody has to turn it on again.</p>}
          <Changes changes={state.changes} toolName={toolName} />
        </section>
      ) : null}
    </div>
  );
}
